import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import {
  User,
  IdTokenResult,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { auth, googleProvider, db } from '../services/firebase'

// User roles
export type UserRole = 'admin' | 'expert' | 'participant' | null;

// Admin emails (hardcoded)
const ADMIN_EMAILS = [
  'vanerven.ux@gmail.com',
  'edna.canedo@gmail.com'
];

// Type for mock user in development bypass mode
interface MockUser extends Partial<User> {
  uid: string
  email: string
  displayName: string
  photoURL: null
  emailVerified: boolean
  isAnonymous: boolean
  metadata: object
  providerData: []
  delete: () => Promise<void>
  getIdToken: () => Promise<string>
  getIdTokenResult: () => Promise<IdTokenResult>
  reload: () => Promise<void>
  toJSON: () => object
}

interface AuthContextType {
  user: User | null
  userRole: UserRole
  loading: boolean
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  bypassLogin: (role?: UserRole) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Fetch expert emails from Firestore
async function fetchExpertEmails(): Promise<string[]> {
  try {
    const docRef = doc(db, 'roles', 'experts');
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.emails || [];
    }
    return [];
  } catch (error) {
    console.warn('[AUTH] Could not fetch expert emails:', error);
    return [];
  }
}

// Determine user role based on email
async function determineUserRole(email: string | null): Promise<UserRole> {
  if (!email) return null;

  // Check if admin
  if (ADMIN_EMAILS.some(adminEmail => adminEmail.toLowerCase() === email.toLowerCase())) {
    return 'admin';
  }

  // Check if expert (from Firestore)
  const expertEmails = await fetchExpertEmails();
  if (expertEmails.some(e => e.toLowerCase() === email.toLowerCase())) {
    return 'expert';
  }

  // Default to participant
  return 'participant';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [userRole, setUserRole] = useState<UserRole>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Configura persistência e listener de estado
    setPersistence(auth, browserLocalPersistence)
      .then(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
          if (currentUser) {
            console.log('[AUTH] Usuário detectado:', currentUser.email);
            setUser(currentUser);

            // Determine role
            const role = await determineUserRole(currentUser.email);
            console.log('[AUTH] Role determinado:', role);
            setUserRole(role);
          } else {
            // Mantém usuário bypass se existir
            setUser((prevUser) => {
              if (prevUser && prevUser.uid === 'dev-bypass-id') {
                // Se for usuário bypass, mantém o user e NÃO reseta o role
                return prevUser;
              }
              // Se não for bypass, limpa tudo (logout real)
              setUserRole(null);
              return null;
            });
          }
          setLoading(false);
        });

        return () => unsubscribe();
      })
      .catch((err) => {
        console.error('[AUTH] Erro persistência:', err);
        setLoading(false);
      });
  }, []);

  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      const result = await signInWithPopup(auth, googleProvider);
      console.log('[AUTH] Login via popup OK:', result.user.email);
      // O onAuthStateChanged vai capturar o usuário automaticamente
    } catch (error) {
      console.error('[AUTH] Erro no login:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido';
      alert(`Erro no login: ${message}`);
      setLoading(false);
    }
  }

  // Função para bypass (dev mode) - pode especificar role
  const bypassLogin = (role: UserRole = 'admin') => {
    console.log('[AUTH] ATIVANDO MODO BYPASS DE DESENVOLVEDOR - Role:', role);
    const devUser: MockUser = {
      uid: 'dev-bypass-id',
      email: role === 'admin' ? ADMIN_EMAILS[0] : `dev-${role}@lumen.tst.jus.br`,
      displayName: `[DEV] ${role === 'admin' ? 'Admin' : role === 'expert' ? 'Especialista' : 'Participante'}`,
      photoURL: null,
      emailVerified: true,
      isAnonymous: false,
      metadata: {},
      providerData: [],
      delete: async () => { },
      getIdToken: async () => 'mock-dev-token',
      getIdTokenResult: async () => ({ token: 'mock-dev-token' } as IdTokenResult),
      reload: async () => { },
      toJSON: () => ({})
    };
    setUser(devUser as User);
    setUserRole(role);
    setLoading(false);
  };

  const signOut = async () => {
    try {
      if (user?.uid === 'dev-bypass-id') {
        setUser(null);
        setUserRole(null);
      } else {
        await firebaseSignOut(auth);
        setUserRole(null);
      }
    } catch (error) {
      console.error('Error signing out:', error)
      // Don't throw - just log the error to avoid unhandled rejections
    }
  }

  const value = {
    user,
    userRole,
    loading,
    signInWithGoogle,
    signOut,
    bypassLogin,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
